import { Injectable } from '@angular/core';
import Utils from '../../../app/shared/utils/utils';
import { GameResultTypeConfigOptions } from '../../../config/game-result-type-config.interface';
import { GameResultCategory } from '../../game/enum/game-result-category.enum';
import { FormControlOptions } from '../interfaces/form-control-options.interface';
import { FormControlSelectOptions } from '../interfaces/form-control-select-options.interface';
import { Dictionary } from '../models/dictionary';
import { GameUtilHelper } from '../models/helper/game-util-helper';
import { ConfigurationService } from '../services/configuration.service';

@Injectable({
  providedIn: 'root'
})
export class GameResultTypeUtils {

  private resultTypeValues;

  constructor(
    private config: ConfigurationService,
    private gameUtilHelper: GameUtilHelper
  ) {
  }

  private getResultTypes(): GameResultTypeConfigOptions {
    if (this.config.isLoadingConfig()) {
      return undefined;
    }

    const config = this.config.getResultTypeConfig();
    if (config) {
      return Utils.clone(config);
    }

    return undefined;
  }

  getTypeConfig(type: number) {
    return this.getResultTypes()[ type ];
  }

  getLabel(resultType: string | number) {
    if (this.getResultTypes() && this.isValid(resultType)) {
      return this.getResultTypes()[ Number(resultType) ].label;
    }

    return '';
  }

  hasField(resultType: string | number, field: string): boolean {
    if (this.getResultTypes()) {
      return Object.keys(this.getResultTypes()[ Number(resultType) ].fields).indexOf(field) >= 0;
    }

    return false;
  }

  getFieldOptions(resultType: string | number, field: string, defaultOptions: FormControlOptions<any>): FormControlOptions<any> {
    if (this.getResultTypes()) {
      const options = this.hasField(resultType, field) ? Utils.clone(this.getResultTypes()[ Number(resultType) ].fields[ field ]) : {};

      for (const optionName in defaultOptions) {
        if (Utils.isDefined(defaultOptions[ optionName ])) {
          options[ optionName ] = Utils.isDefined(options[ optionName ]) ? options[ optionName ] : defaultOptions[ optionName ];
        }
      }

      options.key = defaultOptions.key || field;
      options.hidden = Utils.isDefined(defaultOptions.hidden) ? defaultOptions.hidden : !this.hasField(resultType, field);

      return options;
    }

    return {};
  }

  getTypeValues(): number[] {
    if (this.getResultTypes()) {
      this.resultTypeValues = this.resultTypeValues || Object.keys(this.getResultTypes())
        .filter(Number)
        .map(resultType => Number(resultType));

      return Utils.clone(this.resultTypeValues);
    }

    return [];
  }

  getCategoryOfType(type: number): string {
    const config = this.getResultTypes()[ type ];

    return config && config.category;
  }

  getCategoryValues(): string[] {
    return Object.values(GameResultCategory);
  }

  getDefaultType(): number {
    return this.getTypeValues()[ 0 ];
  }

  getDefaultCategory(): string {
    return this.getCategoryValues()[ 0 ];
  }

  isValid(resultType: string | number): boolean {
    return !isNaN(Number(resultType)) && this.getTypeValues().indexOf(Number(resultType)) >= 0;
  }

  getTypeSelectOptions(): FormControlSelectOptions<number> {
    return this.getTypeValues()
      .map(resultType => {
        return {
          value: resultType,
          label: this.getLabel(resultType)
        };
      });
  }

  getCategorySelectOptions(): FormControlSelectOptions<string> {
    return this.getCategoryValues()
      .map(value => ({ value, label: value.toUpperCase()}));
  }

  getModules(resultType: string | number): string[] {
    if (this.getResultTypes()) {
      return Utils.clone(this.getResultTypes()[ Number(resultType) ].modules);
    }

    return [];
  }

  isModuleEnabled(resultType: string | number, module: string): boolean {
    return this.getModules(resultType).indexOf(module) >= 0;
  }

  createDictionary(levelType: string | number, key: 'parameters' | 'textDictionary' | 'assetDictionary'): any[] {

    const types = this.getResultTypes();

    if (levelType && types) {
      const dict: Dictionary<any> = types[ Number(levelType) ];

      return this.gameUtilHelper.createDictionary(dict, key);
    }

    return [];
  }
}
